export type AssetVariant = "original" | "background" | "content" | "thumbnail";

export interface Asset {
    id: number;
    name: string;
    url_suffix: string;
    url: string;
    content_type: string;
    inserted_at: string;
    reference_type: string;
    reference_id: number;
    filename?: string | null;
    byte_size?: number | null;
    width?: number | null;
    height?: number | null;
    variant?: string | null;
    content_hash?: string | null;
}

export interface AssetListResponse {
    data: Asset[];
}

const VARIANT_ORDER: AssetVariant[] = ["original", "background", "content", "thumbnail"];

export function assetFilename(asset: Asset): string {
    if (asset.filename) return asset.filename;
    const base = asset.name.split("/").pop() || asset.name;
    return base.replace(/-(original|background|content|thumbnail)(\.[^.]+)$/i, "$2");
}

export function assetVariant(asset: Asset): AssetVariant {
    if (asset.variant && VARIANT_ORDER.includes(asset.variant as AssetVariant)) {
        return asset.variant as AssetVariant;
    }

    const stem = (asset.name.split("/").pop() || asset.name).replace(/\.[^.]+$/, "");
    for (const variant of VARIANT_ORDER) {
        if (stem.endsWith(`-${variant}`)) return variant;
    }
    return "original";
}

export function groupAssetsByFilename(assets: Asset[]): { filename: string; variants: Asset[] }[] {
    const groups = new Map<string, Asset[]>();

    for (const asset of assets) {
        const filename = assetFilename(asset);
        const list = groups.get(filename) ?? [];
        list.push(asset);
        groups.set(filename, list);
    }

    return [...groups.entries()].map(([filename, variants]) => ({
        filename,
        variants: [...variants].sort(
            (a, b) => VARIANT_ORDER.indexOf(assetVariant(a)) - VARIANT_ORDER.indexOf(assetVariant(b)),
        ),
    }));
}

export function previewAsset(variants: Asset[]): Asset {
    const byVariant = new Map(variants.map((asset) => [assetVariant(asset), asset]));
    return (
        byVariant.get("thumbnail") ??
        byVariant.get("content") ??
        byVariant.get("original") ??
        firstAsset(variants)
    );
}

export function defaultAsset(variants: Asset[]): Asset {
    const byVariant = new Map(variants.map((asset) => [assetVariant(asset), asset]));
    return byVariant.get("content") ?? byVariant.get("original") ?? firstAsset(variants);
}

function firstAsset(variants: Asset[]): Asset {
    const first = variants[0];
    if (!first) {
        throw new Error("asset group is empty");
    }
    return first;
}
